import { useEffect, useRef, useState } from 'react';
import { startOfDay, subDays } from 'date-fns';
import Select, { StylesConfig } from 'react-select';
import styled from 'styled-components';

import DatePickerShortcuts, { DatePickerShortcutItem } from 'components/insights/filterBar/DatePickerShortcuts';
import DatePicker from 'components/shared/form/DatePicker';
import { colors } from 'styles/theme/colors';
import { LabelSmallBold } from 'styles/typography';
import GearIcon from 'svg/GearIcon';
import SortIcon from 'svg/SortIcon';

import { TaskDateRange, TaskSort } from './taskCards';

const SORT_OPTIONS: { value: TaskSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

function createdPresets(): DatePickerShortcutItem<Date | null>[] {
  const today = startOfDay(new Date());
  return [
    { label: 'Today', value: [today, today] },
    { label: 'Last 7 days', value: [subDays(today, 7), today] },
    { label: 'Last 30 days', value: [subDays(today, 30), today] },
    { label: 'Older than 7 days', value: [null, subDays(today, 7)] },
  ];
}

interface ToolbarProps {
  // The sort control renders only when given a handler; likewise the created range.
  sort?: TaskSort;
  onSortChange?: (sort: TaskSort) => void;
  createdAt?: TaskDateRange;
  onCreatedAtChange?: (range: TaskDateRange) => void;
  onManagePortals?: () => void;
}

export default function TasksToolbar({
  sort,
  onSortChange,
  createdAt,
  onCreatedAtChange,
  onManagePortals,
}: ToolbarProps) {
  return (
    <Bar>
      <LeftGroup>
        {onSortChange ? (
          <SortGroup data-testid='task-sort'>
            <SortIcon />
            <SortLabel htmlFor='task-sort-input'>Sort By:</SortLabel>
            <Select
              inputId='task-sort-input'
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find((option) => option.value === sort)}
              onChange={(option) => option && onSortChange(option.value)}
              isSearchable={false}
              styles={sortSelectStyles}
              components={{ IndicatorSeparator: null, DropdownIndicator: null }}
            />
          </SortGroup>
        ) : (
          <span />
        )}
        {createdAt && onCreatedAtChange && <CreatedRange value={createdAt} onChange={onCreatedAtChange} />}
      </LeftGroup>
      {onManagePortals && (
        <ManageButton type='button' onClick={onManagePortals} data-testid='manage-connected-portals'>
          <GearIcon width={12} height={12} />
          Manage Connected Portals
        </ManageButton>
      )}
    </Bar>
  );
}

// The pickers hold a draft that commits when a calendar closes (or a preset is picked), so
// typing a date by hand doesn't refetch the list on every keystroke.
function CreatedRange({ value, onChange }: { value: TaskDateRange; onChange: (range: TaskDateRange) => void }) {
  const [draft, setDraftState] = useState<TaskDateRange>(value);
  const draftRef = useRef(value);
  const setDraft = (next: TaskDateRange) => {
    draftRef.current = next;
    setDraftState(next);
  };
  useEffect(() => setDraft(value), [value]);
  const presets = createdPresets();

  const commit = (next: TaskDateRange) => {
    setDraft(next);
    onChange(next);
  };
  const commitDraft = () => onChange(draftRef.current);

  const footer = ({ dateInput }: { dateInput: React.RefObject<any> }) => (
    <DatePickerShortcuts<Date | null>
      startDate={draft.start}
      endDate={draft.end}
      datePickerRef={dateInput}
      items={presets}
      setDates={([start, end]) => commit({ start, end })}
    />
  );

  return (
    <CreatedGroup data-testid='task-created-range'>
      <SortLabel>Created:</SortLabel>
      <DatePicker
        selected={draft.start}
        onChange={(date) => setDraft({ ...draftRef.current, start: date })}
        onCalendarClose={commitDraft}
        onBlur={commitDraft}
        maxDate={draft.end ?? undefined}
        isClearable
        placeholder='Start'
        renderFooter={footer}
        popperPlacement='bottom-start'
        data-testid='task-created-start'
      />
      <Emdash>&mdash;</Emdash>
      <DatePicker
        selected={draft.end}
        onChange={(date) => setDraft({ ...draftRef.current, end: date })}
        onCalendarClose={commitDraft}
        onBlur={commitDraft}
        minDate={draft.start ?? undefined}
        isClearable
        placeholder='End'
        renderFooter={footer}
        popperPlacement='bottom-start'
        data-testid='task-created-end'
      />
    </CreatedGroup>
  );
}

// Drawn as plain text in the row; the menu is the styled react-select one rather than the
// browser's own popup.
const sortSelectStyles: StylesConfig<{ value: TaskSort; label: string }, false> = {
  control: (provided) => ({
    ...provided,
    minHeight: 0,
    height: 'auto',
    border: 'none',
    boxShadow: 'none',
    background: 'transparent',
    cursor: 'pointer',
  }),
  valueContainer: (provided) => ({ ...provided, padding: 0 }),
  singleValue: (provided) => ({ ...provided, margin: 0, fontSize: 12, fontWeight: 400, color: colors.black100 }),
  input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
  menu: (provided) => ({ ...provided, minWidth: 120, fontSize: 12 }),
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  /* The ribbon lives inside the scroll container, so it spaces itself off the cards below. */
  margin-bottom: 24px;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const SortGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.black75};
`;

const CreatedGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.black75};

  input {
    width: 110px;
    font-size: 12px;
  }
`;

const Emdash = styled.span`
  color: ${({ theme }) => theme.colors.black50};
`;

const SortLabel = styled(LabelSmallBold)`
  color: ${({ theme }) => theme.colors.black75};
`;

const ManageButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
